import {
  Component,
  Inject,
  Input,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { startWith } from "rxjs";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSelectModule } from "@angular/material/select";

import { Aircraft, Student, User } from "src/app/models";
import { AuthService } from "src/app/service/auth.service";

export type RowType = "aircraft" | "instructor";
type PanelKind = "aircraft" | "instructor" | "student";

export interface Instructor {
  id: string;
  name: string;
}

export interface BookingDialogData {
  id: string;
  rowType: RowType;
  rowId: string;
  rowLabel: string;
  startMin: number;
  endMin: number;
  openMin: number;
  closeMin: number;
  step: number;
  aircraft: Aircraft[];
  instructors: Instructor[];
  students: Student[];
  currentDate: Date;
  aircraftId?: string;
  instructorId?: string;
  studentId?: string;
  createdBy?: User;
  createdAt?: Date;
  updatedAt?: Date;
  secondPosition: boolean;
  update: boolean;
}

@Component({
  selector: "app-reservation-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
  ],
  templateUrl: "./booking-detail.component.html",
  styleUrls: ["./booking-detail.component.scss"],
})
export class BookingDialogComponent implements OnInit, AfterViewInit {
  // Champs formulaire
  dateStart = new Date();
  dateEnd = new Date();
  timeStart = "07:00";
  timeEnd = "08:00";
  createdByName = "";
  createdAt: Date = null;
  updatedAt: Date = null;
  public update = false;

  @Input() public title = "";

  user: User;

  // Valeurs sélectionnées
  aircraftCtrl = new FormControl<string | Aircraft>("");
  instructorCtrl = new FormControl<string | Instructor>("");
  studentCtrl = new FormControl<string | Student>("");

  // Recherches dans panels
  aircraftSearchCtrl = new FormControl<string>("");
  instructorSearchCtrl = new FormControl<string>("");
  studentSearchCtrl = new FormControl<string>("");

  secondCtrl = new FormControl<boolean>(false);

  // Listes pour panels
  filteredAircraftsInPanel: Aircraft[] = [];
  filteredInstructorsInPanel: Instructor[] = [];
  filteredStudentsInPanel: Student[] = [];

  // Refs inputs de recherche (panel)
  @ViewChild("aircraftPanelSearch")
  aircraftPanelSearchRef?: ElementRef<HTMLInputElement>;
  @ViewChild("instructorPanelSearch")
  instructorPanelSearchRef?: ElementRef<HTMLInputElement>;
  @ViewChild("studentPanelSearch")
  studentPanelSearchRef?: ElementRef<HTMLInputElement>;

  // Refs inputs principaux (pour rediriger les frappes / gérer le focus)
  @ViewChild("aircraftInput") aircraftInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild("instructorInput")
  instructorInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild("studentInput") studentInputRef?: ElementRef<HTMLInputElement>;

  // Triggers
  @ViewChild("aircraftInput", { read: MatAutocompleteTrigger })
  aircraftTrigger?: MatAutocompleteTrigger;
  @ViewChild("instructorInput", { read: MatAutocompleteTrigger })
  instructorTrigger?: MatAutocompleteTrigger;
  @ViewChild("studentInput", { read: MatAutocompleteTrigger })
  studentTrigger?: MatAutocompleteTrigger;

  // --- à mettre dans la classe, avec les autres champs ---
  aircraftSearchMouseDown = false;
  instructorSearchMouseDown = false;
  studentSearchMouseDown = false;

  constructor(
    protected authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: BookingDialogData,
    private ref: MatDialogRef<BookingDialogComponent>
  ) {
    // Pré-remplissages
    this.dateStart = new Date(data.currentDate);
    this.dateEnd = new Date(data.currentDate);
    this.timeStart = this.minToTime(data.startMin);
    this.timeEnd = this.minToTime(data.endMin);

    this.createdByName = data.createdBy
      ? `${data.createdBy.lastname} ${data.createdBy.firstname}`
      : "";
    this.createdAt = data.createdAt ?? null;
    this.updatedAt = data.updatedAt ?? null;
    this.update = data.update;

    this.secondCtrl.setValue(this.data.secondPosition ?? false);
    this.user = this.authService.currentUser;

    // Pré-sélections
    if (data.aircraftId) {
      const a = data.aircraft.find((x) => x.id === data.aircraftId);
      if (a) this.aircraftCtrl.setValue(a);
    } else if (data.rowType === "aircraft") {
      const a = data.aircraft.find((x) => x.id === data.rowId);
      if (a) this.aircraftCtrl.setValue(a);
    }
    if (data.instructorId) {
      const i = data.instructors.find((x) => x.id === data.instructorId);
      if (i) this.instructorCtrl.setValue(i);
    } else if (data.rowType === "instructor") {
      const i = data.instructors.find((x) => x.id === data.rowId);
      if (i) this.instructorCtrl.setValue(i);
    } else if (this.user.instructor) {
      const i = data.instructors.find((x) => x.id === this.user.instructor.id);
      if (i) this.instructorCtrl.setValue(i);
    }
    if (data.studentId) {
      const s = data.students.find((x) => x.id === data.studentId);
      if (s) this.studentCtrl.setValue(s);
    }

    // Listes complètes initiales
    this.filteredAircraftsInPanel = data.aircraft.slice();
    this.filteredInstructorsInPanel = data.instructors.slice();
    this.filteredStudentsInPanel = data.students.slice();
  }

  /* ================= Lifecycle ================= */

  ngOnInit(): void {
    // Filtrage live des panneaux
    this.aircraftSearchCtrl.valueChanges
      .pipe(startWith(this.aircraftSearchCtrl.value ?? ""))
      .subscribe(
        (q) =>
          (this.filteredAircraftsInPanel = this.filterAircraftByText(q ?? ""))
      );

    this.instructorSearchCtrl.valueChanges
      .pipe(startWith(this.instructorSearchCtrl.value ?? ""))
      .subscribe(
        (q) =>
          (this.filteredInstructorsInPanel = this.filterInstructorByText(
            q ?? ""
          ))
      );

    this.studentSearchCtrl.valueChanges
      .pipe(startWith(this.studentSearchCtrl.value ?? ""))
      .subscribe(
        (q) =>
          (this.filteredStudentsInPanel = this.filterStudentByText(q ?? ""))
      );
  }

  ngAfterViewInit(): void {
    // 1) A l'ouverture: focus robuste sur l'input de recherche + "défocus" de l'input principal
    this.bindOpenedFocus("aircraft");
    this.bindOpenedFocus("instructor");
    this.bindOpenedFocus("student");

    // 2) Si l'input principal reprend le focus alors que le panel est ouvert → renvoyer le focus au search
    this.redirectFocusWhileOpen("aircraft");
    this.redirectFocusWhileOpen("instructor");
    this.redirectFocusWhileOpen("student");

    // 3) Redirection de frappe (option utile si l'utilisateur tape trop vite)
    this.attachTypeRedirection("aircraft");
    this.attachTypeRedirection("instructor");
    this.attachTypeRedirection("student");

    // 4) Reset à la fermeture (ton HTML appelle déjà (closed), on double aussi ici)
    this.aircraftTrigger?.autocomplete?.closed.subscribe(() =>
      this.resetAircraftSearch()
    );
    this.instructorTrigger?.autocomplete?.closed.subscribe(() =>
      this.resetInstructorSearch()
    );
    this.studentTrigger?.autocomplete?.closed.subscribe(() =>
      this.resetStudentSearch()
    );
  }

  /* ============== Focus & Reset (appelés aussi par le HTML) ============== */

  focusAircraftSearchBox(): void {
    if (this.suppressNextOpen) {
      this.suppressNextOpen = false;
      return;
    }
    setTimeout(() => this.focusSearch("aircraft"), 0);
  }
  focusInstructorSearchBox(): void {
    if (this.suppressNextOpen) {
      this.suppressNextOpen = false;
      return;
    }
    setTimeout(() => this.focusSearch("instructor"), 0);
  }
  focusStudentSearchBox(): void {
    if (this.suppressNextOpen) {
      this.suppressNextOpen = false;
      return;
    }
    setTimeout(() => this.focusSearch("student"), 0);
  }

  resetAircraftSearch(): void {
    this.aircraftSearchCtrl.setValue("");
  }
  resetInstructorSearch(): void {
    this.instructorSearchCtrl.setValue("");
  }
  resetStudentSearch(): void {
    this.studentSearchCtrl.setValue("");
  }

  /* =================== Comportements de focus =================== */

  /** Force le focus dans l'input de recherche à l'ouverture, et sort le focus de l'input principal. */
  private bindOpenedFocus(kind: PanelKind): void {
    const trigger = this.getTrigger(kind);
    if (!trigger) return;

    const searchEl = this.getSearchRef(kind)?.nativeElement;
    const host = this.getHostInput(kind);

    trigger.autocomplete?.opened.subscribe(() => {
      // double rAF pour passer après l’animation interne
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          searchEl?.focus();
          // IMPORTANT : sortir le focus de l'input principal pour éviter le "retour"
          host?.blur();
        })
      );
    });
  }

  /** Si le focus revient sur l'input principal pendant que le panel est ouvert, on le renvoie au search. */
  private redirectFocusWhileOpen(kind: PanelKind): void {
    const host = this.getHostInput(kind);
    const trigger = this.getTrigger(kind);
    const searchEl = this.getSearchRef(kind)?.nativeElement;
    if (!host || !trigger || !searchEl) return;

    host.addEventListener(
      "focus",
      () => {
        if (trigger.panelOpen) {
          // remet immédiatement le focus dans l'input de recherche du panel
          setTimeout(() => searchEl.focus(), 0);
        }
      },
      true // capture → on passe avant tout handler interne
    );
  }

  // Rediriger la 1ère frappe vers la barre de recherche et y injecter la touche
  private attachTypeRedirection(kind: PanelKind): void {
    const host = this.getHostInput(kind);
    const trigger = this.getTrigger(kind);
    const ctrl = this.getSearchCtrl(kind);
    const ref = this.getSearchRef(kind);
    if (!host || !trigger || !ctrl || !ref) return;

    host.addEventListener("keydown", (evt: KeyboardEvent) => {
      if (!trigger.panelOpen) return;

      const isChar =
        evt.key.length === 1 && !evt.ctrlKey && !evt.metaKey && !evt.altKey;
      const isBackspace = evt.key === "Backspace";
      if (!isChar && !isBackspace) return;

      evt.preventDefault();
      ref.nativeElement.focus();

      const val = ctrl.value ?? "";
      ctrl.setValue(isBackspace ? val.slice(0, -1) : val + evt.key);
    });
  }

  /* =================== Filtrage =================== */

  private filterAircraftByText(query: string): Aircraft[] {
    const q = (query || "").toLowerCase().trim();
    if (!q) return this.data.aircraft;
    return this.data.aircraft.filter((a) => a.code.toLowerCase().includes(q));
  }

  private filterInstructorByText(query: string): Instructor[] {
    const q = (query || "").toLowerCase().trim();
    if (!q) return this.data.instructors;
    return this.data.instructors.filter((i) =>
      i.name.toLowerCase().includes(q)
    );
  }

  private filterStudentByText(query: string): Student[] {
    const q = (query || "").toLowerCase().trim();
    if (!q) return this.data.students;
    return this.data.students.filter((s) => s.name.toLowerCase().includes(q));
  }

  /* =================== displayWith =================== */

  displayAircraft(a: Aircraft | null): string {
    return a ? `${a.code}` : "";
  }
  displayInstructor(i: Instructor | null): string {
    return i ? i.name : "";
  }
  displayStudent(s: Student | null): string {
    return s ? s.name : "";
  }

  /* =================== Actions =================== */

  cancel(): void {
    this.ref.close({ action: "cancel" });
  }

  save(): void {
    const aVal = this.aircraftCtrl.value;
    const iVal = this.instructorCtrl.value;
    let sVal = this.studentCtrl.value;

    if (sVal === "" && this.user.role === "USER") {
      sVal = String(this.user.student?.id ?? null);
    }

    const aircraftId =
      typeof aVal === "object" ? (aVal as Aircraft)?.id ?? null : null;
    const instructorId =
      typeof iVal === "object" ? (iVal as Instructor)?.id ?? null : null;
    const studentId =
      typeof sVal === "object"
        ? (sVal as Student)?.id ?? null
        : (sVal as string) ?? null;

    const start = this.timeToMin(this.timeStart);
    const end = this.timeToMin(this.timeEnd);

    this.ref.close({
      action: "save",
      rowType: aircraftId ? "aircraft" : "instructor",
      rowId: aircraftId || instructorId,
      startMin: start,
      endMin: end,
      aircraftId,
      instructorId,
      studentId,
      secondPosition: this.secondCtrl.value === true,
      startDate: this.dateStart,
      endDate: this.dateEnd,
    });
  }

  /* =================== Utils =================== */

  private getTrigger(kind: PanelKind): MatAutocompleteTrigger | undefined {
    return kind === "aircraft"
      ? this.aircraftTrigger
      : kind === "instructor"
      ? this.instructorTrigger
      : this.studentTrigger;
  }

  private getSearchCtrl(kind: PanelKind): FormControl<string> {
    return kind === "aircraft"
      ? this.aircraftSearchCtrl
      : kind === "instructor"
      ? this.instructorSearchCtrl
      : this.studentSearchCtrl;
  }

  private getSearchRef(
    kind: PanelKind
  ): ElementRef<HTMLInputElement> | undefined {
    return kind === "aircraft"
      ? this.aircraftPanelSearchRef
      : kind === "instructor"
      ? this.instructorPanelSearchRef
      : this.studentPanelSearchRef;
  }

  private getHostInput(kind: PanelKind): HTMLInputElement | undefined {
    return kind === "aircraft"
      ? this.aircraftInputRef?.nativeElement
      : kind === "instructor"
      ? this.instructorInputRef?.nativeElement
      : this.studentInputRef?.nativeElement;
  }

  private timeToMin(timeHHMM: string): number {
    const [h, m] = timeHHMM.split(":").map(Number);
    return h * 60 + m;
  }

  private minToTime(min: number): string {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }

  /** Focus the search input reliably on iOS + place caret at end */
  private focusSearch(kind: "aircraft" | "instructor" | "student") {
    const el =
      kind === "aircraft"
        ? this.aircraftPanelSearchRef?.nativeElement
        : kind === "instructor"
        ? this.instructorPanelSearchRef?.nativeElement
        : this.studentPanelSearchRef?.nativeElement;
    if (!el) return;

    // 2 x rAF lets Material finish its open animation first (iOS needs this)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Important: focus first, THEN set caret
        el.focus({ preventScroll: true });
        try {
          const len = el.value?.length ?? 0;
          el.setSelectionRange(len, len);
        } catch {}
      });
    });
  }

  private getValueCtrl(kind: "aircraft" | "instructor" | "student") {
    return kind === "aircraft"
      ? this.aircraftCtrl
      : kind === "instructor"
      ? this.instructorCtrl
      : this.studentCtrl;
  }

  private suppressNextOpen = false;
  clearSelection(kind: "aircraft" | "instructor" | "student"): void {
    // vider la valeur sélectionnée
    this.getValueCtrl(kind).setValue(null);
    // vider la recherche
    this.getSearchCtrl(kind).setValue("");

    // fermer explicitement le panel si ouvert
    this.getTrigger(kind)?.closePanel();

    // bloquer l’ouverture immédiate
    this.suppressNextOpen = true;

    // redonner focus au champ principal
    this.getHostInput(kind)?.blur(); // 👈 on enlève le focus
    // (optionnel : si tu veux garder le caret, tu peux remettre focus après un délai)
  }
}
